import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { Alert } from '@app/ui/alert';
import { Label } from '@app/ui/label';
import { Tooltip } from '@app/ui/tooltip';
import { Loader } from '@app/ui/loader';

import { FIELD_CATALOGUE_GET_MANY, MATCHING_RULES_PREVIEW_GET_ONE, MATCHING_RULE_DELETE_ONE } from '../../gql';
import {
  MatchingRule,
  MatchingRuleKind,
  MatchingRulesPreview,
  RuleFamily,
  RuleField,
  RuleSetVersion,
  RuleType,
} from '../../types';
import { MatchingRuleEditor } from './MatchingRuleEditor';
import { ColorItemDeleteDialog } from '../color/ColorItemDeleteDialog';

export interface MatchingRulesPanelProps {
  readonly version: RuleSetVersion;
  readonly editable: boolean;
  readonly onChanged: () => Promise<unknown> | void;
}

const NO_FIELDS: RuleField[] = [];

const SECTION_KINDS: MatchingRuleKind[] = [
  MatchingRuleKind.exclusion,
  MatchingRuleKind.blocking,
  MatchingRuleKind.compatibility,
];

// The matching tab's three sections (SPEC §7.7) — replaces the watchlist's single `RuleList` /
// colour's `ColorRubricPanel` inside `FamilyRulesPanel`'s frame, mirroring the same row/dialog
// shape. Row counts are small (25 seed rules), so this is plain rows, not the shared DataGrid
// `Table` (frontend-ui.md: its row-height/padding traps only matter at DataGrid scale).
export const MatchingRulesPanel = ({ version, editable, onChanged }: MatchingRulesPanelProps) => {
  const { t } = useTranslation('common');

  const [editingRule, setEditingRule] = useState<MatchingRule | 'new' | null>(null);
  const [newRuleKind, setNewRuleKind] = useState<MatchingRuleKind>(MatchingRuleKind.compatibility);
  const [deletingRule, setDeletingRule] = useState<MatchingRule | null>(null);

  const { data: catalogueData } = useQuery<{ fieldCatalogueGetMany: RuleField[] }>(FIELD_CATALOGUE_GET_MANY, {
    variables: { family: RuleFamily.matching },
  });
  const fields = catalogueData?.fieldCatalogueGetMany ?? NO_FIELDS;

  const {
    data: previewData,
    loading: previewLoading,
    refetch: refetchPreview,
  } = useQuery<{ matchingRulesPreviewGetOne: MatchingRulesPreview }>(MATCHING_RULES_PREVIEW_GET_ONE, {
    variables: { ruleSetVersionId: version.id },
    fetchPolicy: 'cache-and-network',
  });
  const preview = previewData?.matchingRulesPreviewGetOne;

  const allRules = version.matchingRules ?? [];
  const rulesByKind = useMemo(() => {
    const map = new Map<MatchingRuleKind, MatchingRule[]>();

    SECTION_KINDS.forEach(kind =>
      map.set(
        kind,
        allRules.filter(rule => rule.kind === kind).sort((a, b) => a.ordinal - b.ordinal),
      ),
    );

    return map;
  }, [allRules]);

  const refresh = async () => {
    await Promise.all([onChanged(), refetchPreview()]);
  };

  const openNewRule = (kind: MatchingRuleKind) => {
    setNewRuleKind(kind);
    setEditingRule('new');
  };

  return (
    <Box>
      {/* PLAN P2-5/P2-6 — the SAME server-authored weights total the publish dialog reads
      (`matchingRulesPreviewGetOne`, never re-derived on the client). The total is display-only
      (V-1 retired — BA ruling 2026-09-16, the score is a ratio, not a percentage of 100). */}
      {!previewLoading && preview && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
            {t('rules.matching.preview.weightTotalLabel', { total: preview.activeWeightTotal })}
          </Typography>

          {preview.blocking.map(message => (
            <Alert key={message} severity="error" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}

          {preview.warnings.map(message => (
            <Alert key={message} severity="warning" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}
        </Box>
      )}

      {SECTION_KINDS.map((kind, index) => {
        const rules = rulesByKind.get(kind) ?? [];

        return (
          <Box key={kind}>
            {index > 0 && <Divider sx={{ my: 3 }} />}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h2">{t(`rules.matching.section.${kind}`)}</Typography>
              {editable && (
                <Button size="small" startIcon={<AddIcon />} onClick={() => openNewRule(kind)}>
                  {t('rules.matching.rule.addButton')}
                </Button>
              )}
            </Box>

            {!rules.length && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('rules.matching.section.empty')}
              </Typography>
            )}

            {rules.map(rule => (
              <Box
                key={rule.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'grey.300',
                }}
              >
                <Typography variant="body2" sx={{ fontFamily: 'monospace', minWidth: 90 }}>
                  {rule.ruleId}
                </Typography>
                <Label
                  text={t(`rules.type.option.${rule.type}`, rule.type)}
                  color={rule.type === RuleType.ai ? 'info' : 'grey'}
                  sx={{ lineHeight: 'normal' }}
                />
                <Label
                  text={rule.enabled ? t('general.option.yes') : t('general.option.no')}
                  color={rule.enabled ? 'success' : 'grey'}
                  sx={{ lineHeight: 'normal' }}
                />
                {rule.weight != null && (
                  <Label
                    text={t('rules.matching.rule.weightLabel', { count: rule.weight })}
                    color="grey"
                    sx={{ lineHeight: 'normal' }}
                  />
                )}
                <Typography variant="body1" sx={{ flexGrow: 1, minWidth: 200 }}>
                  {rule.name}
                </Typography>

                {editable && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={t('rules.list.editTooltip') ?? undefined}>
                      <IconButton size="small" onClick={() => setEditingRule(rule)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('rules.delete.button') ?? undefined}>
                      <IconButton size="small" color="error" onClick={() => setDeletingRule(rule)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        );
      })}

      {editingRule && (
        <MatchingRuleEditor
          open
          onClose={() => setEditingRule(null)}
          ruleSetVersionId={version.id}
          rule={editingRule === 'new' ? null : editingRule}
          initialKind={newRuleKind}
          fields={fields}
          allRules={allRules}
          onSaved={refresh}
        />
      )}

      {deletingRule && (
        <ColorItemDeleteDialog
          open
          onClose={() => setDeletingRule(null)}
          mutation={MATCHING_RULE_DELETE_ONE}
          id={deletingRule.id}
          itemText={deletingRule.name}
          dialogTitle={t('rules.delete.dialogTitle', { ruleId: deletingRule.ruleId }) ?? ''}
          successMessage={t('rules.delete.successMessage', { ruleId: deletingRule.ruleId }) ?? ''}
          onDeleted={async () => {
            setDeletingRule(null);
            await refresh();
          }}
        />
      )}

      <Loader isLoading={previewLoading && !previewData} />
    </Box>
  );
};
